import { Check, Library } from 'lucide-react'
import { useEffect, useRef, useState, type RefObject } from 'react'
import { catalogShowLabel, type CatalogShow } from './catalogShowPicker'

type Props = {
  shows: CatalogShow[]
  selectedSeries: string | null
  loading: boolean
  error: string
  onChoose: (series: string) => void
  onRetry: () => void
  selectedButtonRef: RefObject<HTMLButtonElement | null>
}

export default function EpisodeShowPicker({ shows, selectedSeries, loading, error, onChoose, onRetry, selectedButtonRef }: Props) {
  const [expanded, setExpanded] = useState(!selectedSeries)
  const firstShowButtonRef = useRef<HTMLButtonElement | null>(null)
  const initialRosterFocusRef = useRef(!selectedSeries)
  const pendingFocusRef = useRef<'roster' | 'selected' | null>(null)
  useEffect(() => {
    if (!initialRosterFocusRef.current || selectedSeries || !expanded || loading || error || shows.length === 0) return
    initialRosterFocusRef.current = false
    firstShowButtonRef.current?.focus()
  }, [selectedSeries, expanded, loading, error, shows])
  useEffect(() => {
    const pending = pendingFocusRef.current
    pendingFocusRef.current = null
    if (expanded && pending === 'roster') firstShowButtonRef.current?.focus()
    if (!expanded && pending === 'selected') selectedButtonRef.current?.focus()
  }, [expanded, selectedButtonRef])

  function changeShow() {
    pendingFocusRef.current = 'roster'
    setExpanded(true)
  }

  function returnToEpisodes() {
    pendingFocusRef.current = 'selected'
    setExpanded(false)
  }
  return (
    <section className="episode-show-picker" aria-label="Choose an episode show">
      {selectedSeries && !expanded ? <div className="episode-show-selected">
        <Library size={19} aria-hidden="true" />
        <span><small>Selected show</small><strong>{catalogShowLabel(selectedSeries)}</strong></span>
        <button ref={selectedButtonRef} type="button" onClick={changeShow}>Change show</button>
      </div> : <>
        <h3>{selectedSeries ? 'Choose a show' : 'Choose a show to see episodes'}</h3>
        <p>Browse one show at a time, then pick an episode from its seasons.</p>
      </>}
      {loading && <p role="status" className="episode-show-status">Loading the full show list…</p>}
      {error && <div className="episode-show-error" role="alert"><p>{error}</p><button type="button" onClick={onRetry}>Retry show list</button></div>}
      {!loading && !error && shows.length === 0 && <p role="status" className="episode-show-status">No indexed shows are available right now.</p>}
      {!loading && !error && expanded && <div className="episode-show-grid" role="group" aria-label="Indexed shows">
        {shows.map(show => <button
          type="button"
          key={show.series}
          ref={show.series === shows[0]?.series ? firstShowButtonRef : undefined}
          aria-pressed={selectedSeries === show.series}
          aria-label={`Browse ${catalogShowLabel(show.series)} episodes`}
          onClick={() => { pendingFocusRef.current = 'selected'; onChoose(show.series); setExpanded(false) }}
        >
          <span className="episode-show-icon">{selectedSeries === show.series ? <Check size={17} /> : <Library size={17} />}</span>
          <span><strong>{catalogShowLabel(show.series)}</strong><small>{show.episode_count} indexed episodes</small></span>
        </button>)}
      </div>}
      {selectedSeries && expanded && <button className="episode-show-return" type="button" onClick={returnToEpisodes}>Back to {catalogShowLabel(selectedSeries)} episodes</button>}
    </section>
  )
}
